// User interaction handlers for graph visualization

/**
 * Add opacity to a hex color
 * @param {string} hexColor - Hex color string
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} RGBA color string
 */
const addOpacityToColor = (hexColor, opacity) => {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Darken a hex color by a percentage
 * @param {string} hexColor - Hex color string
 * @param {number} amount - Amount to darken (0-1)
 * @returns {string} Darkened hex color
 */
const darkenColor = (hexColor, amount) => {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  
  // Darken by reducing each component
  const newR = Math.round(r * (1 - amount));
  const newG = Math.round(g * (1 - amount));
  const newB = Math.round(b * (1 - amount));
  
  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Setup click interactions for nodes with double-click handling
 * @param {Object} sigma - Sigma.js instance
 * @param {Function} onNodeClick - Callback function for node clicks
 * @returns {Function} Cleanup function
 */
export const setupClickInteractions = (sigma, onNodeClick) => {
  let clickTimeout = null;
  let clickCount = 0;

  const handleNodeClick = (event) => {
    clickCount++;
    
    if (clickCount === 1) {
      // Start timer for single click
      clickTimeout = setTimeout(() => {
        // Single click - open modal
        onNodeClick(event.node);
        clickCount = 0;
      }, 300); // 300ms delay to wait for potential double click
    } else if (clickCount === 2) {
      // Double click - zoom to node
      clearTimeout(clickTimeout);
      clickCount = 0;
      
      // Get node position
      const nodeDisplayData = sigma.getNodeDisplayData(event.node);
      if (nodeDisplayData) {
        // Zoom to the node with animation
        sigma.getCamera().animate(
          {
            x: nodeDisplayData.x,
            y: nodeDisplayData.y,
            ratio: 0.2 // Zoom level (smaller = more zoomed in)
          },
          {
            duration: 500, // Animation duration in ms
            easing: 'quadInOut'
          }
        );
      }
    }
  };

  const handleStageDoubleClick = () => {
    // Zoom out to fit the entire graph
    const camera = sigma.getCamera();
    const graphBbox = sigma.getBBox();
    
    if (graphBbox) {
      // Calculate the ratio needed to fit the entire graph with some padding
      const containerBounds = sigma.getContainer().getBoundingClientRect();
      const graphWidth = graphBbox.x[1] - graphBbox.x[0];
      const graphHeight = graphBbox.y[1] - graphBbox.y[0];
      
      const containerAspect = containerBounds.width / containerBounds.height;
      const graphAspect = graphWidth / graphHeight;
      
      let targetRatio;
      if (containerAspect > graphAspect) {
        // Container is wider than graph
        targetRatio = (graphHeight * 1.2) / containerBounds.height;
      } else {
        // Container is taller than graph
        targetRatio = (graphWidth * 1.2) / containerBounds.width;
      }
      
      camera.animate(
        {
          x: (graphBbox.x[0] + graphBbox.x[1]) / 2,
          y: (graphBbox.y[0] + graphBbox.y[1]) / 2,
          ratio: Math.max(targetRatio, 0.1) // Ensure we don't zoom in too much
        },
        {
          duration: 500,
          easing: 'quadInOut'
        }
      );
    }
  };

  // Add event listeners
  sigma.on('clickNode', handleNodeClick);
  sigma.on('doubleClickStage', handleStageDoubleClick);

  // Return cleanup function
  return () => {
    if (clickTimeout) {
      clearTimeout(clickTimeout);
    }
    sigma.off('clickNode', handleNodeClick);
    sigma.off('doubleClickStage', handleStageDoubleClick);
  };
};

/**
 * Setup hover interactions for nodes with pill shape effect and connected node/edge highlighting
 * @param {Object} sigma - Sigma.js instance
 * @param {Object} graph - Graphology graph instance
 * @returns {Function} Cleanup function
 */
export const setupHoverInteractions = (sigma, graph) => {
  const handleNodeEnter = (event) => {
    document.body.style.cursor = 'pointer';
    
    const nodeId = event.node;
    
    // Store original attributes for restoration
    const originalSize = graph.getNodeAttribute(nodeId, 'size');
    const originalColor = graph.getNodeAttribute(nodeId, 'color');
    
    // Get connected nodes and edges
    const connectedNodes = new Set();
    const connectedEdges = new Set();
    
    // Find all neighbors and edges
    graph.forEachNeighbor(nodeId, (neighbor) => {
      connectedNodes.add(neighbor);
    });
    
    graph.forEachEdge(nodeId, (edgeId, attributes, source, target) => {
      connectedEdges.add(edgeId);
    });
    
    // Store original states for all affected nodes and edges
    const originalStates = {
      nodes: new Map(),
      edges: new Map()
    };
    
    // Store original node states
    graph.forEachNode((node, attributes) => {
      originalStates.nodes.set(node, {
        color: attributes.color,
        size: attributes.size,
        opacity: 1.0
      });
    });
    
    // Store original edge states
    graph.forEachEdge((edge, attributes) => {
      originalStates.edges.set(edge, {
        color: attributes.color,
        size: attributes.size,
        opacity: 1.0
      });
    });
    
    // Apply hover effect to main node
    graph.setNodeAttribute(nodeId, 'size', originalSize * 1.4);
    graph.setNodeAttribute(nodeId, 'highlighted', true);
    graph.setNodeAttribute(nodeId, 'borderSize', 3);
    graph.setNodeAttribute(nodeId, 'borderColor', '#ffffff');
    graph.setNodeAttribute(nodeId, 'hoverState', {
      originalSize,
      originalColor,
      pillEffect: true,
      originalStates
    });
    
    // Dim all nodes and edges first
    graph.forEachNode((node) => {
      if (node !== nodeId && !connectedNodes.has(node)) {
        const currentColor = graph.getNodeAttribute(node, 'color');
        graph.setNodeAttribute(node, 'color', addOpacityToColor(currentColor, 0.2));
      }
    });
    
    graph.forEachEdge((edge) => {
      if (!connectedEdges.has(edge)) {
        const currentColor = graph.getEdgeAttribute(edge, 'color');
        graph.setEdgeAttribute(edge, 'color', addOpacityToColor(currentColor, 0.2));
      }
    });
    
    // Highlight connected nodes
    connectedNodes.forEach((nodeId) => {
      const currentSize = graph.getNodeAttribute(nodeId, 'size');
      graph.setNodeAttribute(nodeId, 'size', currentSize * 1.2);
      graph.setNodeAttribute(nodeId, 'borderSize', 2.5);
      graph.setNodeAttribute(nodeId, 'borderColor', '#FFD700'); // Gold border for connected nodes
    });
    
    // Highlight connected edges
    connectedEdges.forEach((edgeId) => {
      const currentSize = graph.getEdgeAttribute(edgeId, 'size');
      graph.setEdgeAttribute(edgeId, 'size', currentSize * 2); // Make edges thicker
      // Keep original edge color but make it more prominent
      const currentColor = graph.getEdgeAttribute(edgeId, 'color');
      graph.setEdgeAttribute(edgeId, 'color', darkenColor(currentColor, 0.3));
    });
    
    sigma.refresh();
  };

  const handleNodeLeave = (event) => {
    document.body.style.cursor = 'default';
    
    const nodeId = event.node;
    const hoverState = graph.getNodeAttribute(nodeId, 'hoverState');
    
    if (hoverState && hoverState.originalStates) {
      // Restore all node states
      hoverState.originalStates.nodes.forEach((originalState, node) => {
        graph.setNodeAttribute(node, 'color', originalState.color);
        graph.setNodeAttribute(node, 'size', originalState.size);
        graph.setNodeAttribute(node, 'borderSize', 2); // Reset to default
        graph.setNodeAttribute(node, 'borderColor', '#fff'); // Reset to default
      });
      
      // Restore all edge states
      hoverState.originalStates.edges.forEach((originalState, edge) => {
        graph.setEdgeAttribute(edge, 'color', originalState.color);
        graph.setEdgeAttribute(edge, 'size', originalState.size);
      });
      
      // Reset main node
      graph.setNodeAttribute(nodeId, 'highlighted', false);
      graph.removeNodeAttribute(nodeId, 'hoverState');
    }
    
    sigma.refresh();
  };

  // Add event listeners
  sigma.on('enterNode', handleNodeEnter);
  sigma.on('leaveNode', handleNodeLeave);

  // Return cleanup function
  return () => {
    sigma.off('enterNode', handleNodeEnter);
    sigma.off('leaveNode', handleNodeLeave);
  };
};

/**
 * Setup zoom-based label updates
 * @param {Object} sigma - Sigma.js instance
 * @returns {Function} Cleanup function
 */
export const setupZoomInteractions = (sigma) => {
  const handleCameraUpdate = () => {
    sigma.refresh({ skipIndexation: true });
  };

  // Add camera event listener to update labels on zoom
  sigma.getCamera().on('updated', handleCameraUpdate);

  // Return cleanup function
  return () => {
    sigma.getCamera().off('updated', handleCameraUpdate);
  };
};

/**
 * Setup all interactions for the graph
 * @param {Object} sigma - Sigma.js instance
 * @param {Object} graph - Graphology graph instance
 * @param {Function} onNodeClick - Callback function for node clicks
 * @returns {Function} Cleanup function for all interactions
 */
export const setupAllInteractions = (sigma, graph, onNodeClick) => {
  const cleanupClick = setupClickInteractions(sigma, onNodeClick);
  const cleanupHover = setupHoverInteractions(sigma, graph); // Restored hover for labels
  const cleanupZoom = setupZoomInteractions(sigma);

  // Return combined cleanup function
  return () => {
    cleanupClick();
    cleanupHover();
    cleanupZoom();
  };
};
